import React, { useCallback, useEffect, useState } from 'react';
import qs from 'query-string';
import { Location, useNavigate } from 'react-router-dom';
import {
  fetchInvitesEffect,
  getInviteByTokenEffect,
  transferInvitesEffect,
} from 'app/store/Notifications/effects';
import { signOutEffect } from 'app/store/Auth/effects';
import { useAppDispatch } from 'app/store';
import { useIsMounted } from 'app/utils/hooks';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
} from '@chakra-ui/react';

export interface InviteTransferDialogComponentProps {
  displayName?: string;
  open: boolean;
  onClose: () => void;
  transferInvites: () => Promise<void>;
  signOut: () => Promise<void>;
  loading?: boolean;
}

export const InviteTransferDialogComponent: React.FC<InviteTransferDialogComponentProps> = ({
  open,
  displayName,
  onClose,
  transferInvites,
  loading,
  signOut,
}) => (
  <Modal isOpen={open} onClose={onClose} data-testid="dialog">
    <ModalOverlay />
    <ModalContent>
      <ModalHeader data-testid="dialog-title" title="Invite Transfer">
        Invite Transfer
      </ModalHeader>
      <ModalBody data-testid="dialog-content">
        <Text fontSize="md">
          We see that you are logged into Session as {displayName}
        </Text>
        &nbsp;
        <Text fontSize="md">
          Do you want to transfer the Song invite to this account? If not,
          please sign-out if you want to accept the Song invite with another
          user account.
        </Text>
      </ModalBody>
      <ModalFooter data-testid="dialog-actions">
        <Button
          variant="text"
          onClick={signOut}
          isDisabled={loading}
          mr="auto"
          p={3}
          data-testid="InviteTransferDialog-logout"
        >
          Sign out
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="InviteTransferDialog-close"
        >
          Close
        </Button>
        <Button
          ml={2}
          onClick={transferInvites}
          isDisabled={loading}
          data-testid="InviteTransferDialog-transfer"
        >
          Transfer
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

export interface InviteTransferDialogProps {
  location: Location;
  displayName?: string;
}

export const InviteTransferDialog: React.FC<InviteTransferDialogProps> = ({
  location,
  displayName,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inviteToken = qs.parse(location.search, { decode: false })
    ?.inviteToken as string | null;

  const handleClose = useCallback(
    (props?: { keepSearchParams: boolean }) => {
      if (!props?.keepSearchParams) {
        navigate({ search: '' }, { replace: true });
      }
      setOpen(false);
    },
    [navigate]
  );

  const transferInvites = useCallback(async () => {
    if (inviteToken) {
      try {
        setLoading(true);
        await dispatch(
          transferInvitesEffect({ inviteToken: inviteToken.toString() })
        ).unwrap();
        await dispatch(fetchInvitesEffect({})).unwrap();
        handleClose();
      } catch (e) {
        // Proceed regardless
      } finally {
        setLoading(false);
      }
    }
  }, [handleClose, inviteToken, dispatch]);

  const signOut = useCallback(async () => {
    if (inviteToken) {
      try {
        setLoading(true);
        await dispatch(signOutEffect({ pathname: location.pathname })).unwrap();
        handleClose({ keepSearchParams: true });
        navigate(
          { pathname: '/', search: location.search },
          { state: { from: location.pathname } }
        );
      } catch (e) {
        // Proceed regardless
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, inviteToken]);

  const fetchInviteByToken = useCallback(async () => {
    if (inviteToken) {
      try {
        const invite = await dispatch(
          getInviteByTokenEffect({ inviteToken })
        ).unwrap();
        if (invite._links?.transferInvite) {
          setOpen(true);
        }
      } catch (e) {
        navigate({ search: '' }, { replace: true });
      }
    }
  }, [dispatch, inviteToken, navigate]);

  useEffect(() => {
    fetchInviteByToken();
  }, [fetchInviteByToken]);

  return (
    <InviteTransferDialogComponent
      displayName={displayName}
      onClose={handleClose}
      open={open}
      transferInvites={transferInvites}
      loading={loading}
      signOut={signOut}
    />
  );
};
